import { useState, useEffect } from 'react';
import ShopModal from '../../components/admin/ShopModal';
import { Plus, Search, CreditCard as Edit2, Trash2, MapPin, Square, Building2, Store, ShoppingBag, Utensils, Laptop, ShoppingCart, Scissors, BookOpen, Dumbbell, Gem, Home } from 'lucide-react';
import { apiService } from '../../services/api';

export default function AdminShops() {
  const [shops, setShops] = useState<any[]>([]);
  const [filteredShops, setFilteredShops] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingShop, setEditingShop] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadShops();
  }, []);

  const loadShops = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getShops({ page: 1, limit: 100 });
      
      if (response.status === 'success' && response.data?.shops) {
        setShops(response.data.shops);
        setFilteredShops(response.data.shops);
      } else {
        setError('Failed to load shops');
      }
    } catch (err) {
      console.error('Error loading shops:', err);
      setError('Failed to load shops. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = shops.filter(shop => {
      const matchesSearch = shop.shopNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           shop.purpose.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !statusFilter || shop.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    setFilteredShops(filtered);
  }, [shops, searchTerm, statusFilter]);

  const handleAddShop = () => {
    setEditingShop(null);
    setShowModal(true);
  };

  const handleEditShop = (shop: any) => {
    setEditingShop(shop);
    setShowModal(true);
  };

  const handleDeleteShop = async (shopId: string) => {
    if (!shopId || shopId === 'undefined') {
      alert('Invalid shop ID');
      return;
    }
    
    if (confirm('Are you sure you want to delete this shop?')) {
      try {
        const response = await apiService.deleteShop(shopId);
        if (response.status === 'success') {
          setShops(shops.filter(shop => (shop.id || shop._id) !== shopId));
        } else {
          alert('Failed to delete shop');
        }
      } catch (error) {
        console.error('Error deleting shop:', error);
        alert('Failed to delete shop. Please try again.');
      }
    }
  };

  const handleSaveShop = async (shopData: any) => {
    try {
      console.log('Saving shop with data:', shopData);
      
      let response;
      if (editingShop) {
        // Update existing shop
        const shopId = editingShop.id || editingShop._id;
        if (!shopId) {
          alert('Invalid shop ID for editing');
          return;
        }
        response = await apiService.updateShop(shopId, shopData);
      } else {
        // Add new shop
        response = await apiService.createShop(shopData);
      }

      if (response.status === 'success') {
        // Reload shops to get updated data
        await loadShops();
        setShowModal(false);
      } else {
        alert('Failed to save shop');
      }
    } catch (error: any) {
      console.error('Error saving shop:', error);
      
      // Show specific validation errors if available
      if (error.message && error.message.includes('Validation failed')) {
        alert('Validation failed. Please check all required fields and try again.');
      } else {
        alert(`Failed to save shop: ${error.message || 'Please try again.'}`);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'reserved': return 'bg-yellow-100 text-yellow-800';
      case 'rented': return 'bg-blue-100 text-blue-800';
      case 'unavailable': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getShopIcon = (purpose: string) => {
    const iconMap: { [key: string]: JSX.Element } = {
      'Pharmacy': <Building2 className="h-16 w-16 text-primary" />,
      'Clothing Store': <ShoppingBag className="h-16 w-16 text-primary" />,
      'Confectionary': <Utensils className="h-16 w-16 text-primary" />,
      'Electronics': <Laptop className="h-16 w-16 text-primary" />,
      'Groceries': <ShoppingCart className="h-16 w-16 text-primary" />,
      'Beauty Salon': <Scissors className="h-16 w-16 text-primary" />,
      'Bookstore': <BookOpen className="h-16 w-16 text-primary" />,
      'Sports Goods': <Dumbbell className="h-16 w-16 text-primary" />,
      'Jewelry': <Gem className="h-16 w-16 text-primary" />,
      'Home & Garden': <Home className="h-16 w-16 text-primary" />,
      'Office': <Building2 className="h-16 w-16 text-primary" />,
      'Gaming Hub': <Laptop className="h-16 w-16 text-primary" />,
      'Mens Wear': <ShoppingBag className="h-16 w-16 text-primary" />,
      'Womens Wear': <ShoppingBag className="h-16 w-16 text-primary" />,
      'Childrens Wear': <ShoppingBag className="h-16 w-16 text-primary" />,
      'Baby Wear': <ShoppingBag className="h-16 w-16 text-primary" />,
      'Shoes': <ShoppingBag className="h-16 w-16 text-primary" />,
      'Bags': <ShoppingBag className="h-16 w-16 text-primary" />,
      'Accessories': <Gem className="h-16 w-16 text-primary" />,
      'Watches': <Gem className="h-16 w-16 text-primary" />,
      'Other': <Store className="h-16 w-16 text-primary" />
    };
    return iconMap[purpose] || <Store className="h-16 w-16 text-primary" />;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p className="text-gray-600">Loading shops...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-red-500 mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Shops</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadShops}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Shop Management</h1>
            <p className="text-sm sm:text-base text-gray-600">Manage all retail spaces in Gandi Mall</p>
          </div>
          <button
            onClick={handleAddShop}
            className="bg-primary text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors btn-hover flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="hidden sm:inline">Add New Shop</span>
            <span className="sm:hidden">Add Shop</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
              <input
                type="text"
                placeholder="Search shops..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
            >
              <option value="">All Status</option>
              <option value="available">Available</option>
              <option value="reserved">Reserved</option>
              <option value="rented">Rented</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </div>
        </div>

        {/* Shops Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredShops.map((shop, index) => (
            <div key={shop.id || shop._id || `shop-${index}`} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="relative bg-gray-50 p-4 sm:p-6 lg:p-8 flex items-center justify-center">
                <div className="h-12 w-12 sm:h-16 sm:w-16 lg:h-20 lg:w-20">
                  {getShopIcon(shop.purpose)}
                </div>
                <div className="absolute top-2 sm:top-3 right-2 sm:right-3">
                  <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium ${getStatusColor(shop.status)}`}>
                    {shop.status.charAt(0).toUpperCase() + shop.status.slice(1)}
                  </span>
                </div>
              </div>
              
              <div className="p-3 sm:p-4">
                <div className="flex justify-between items-start mb-2 sm:mb-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900">Shop {shop.shopNumber}</h3>
                  {shop.rent && (
                    <div className="text-base sm:text-lg font-bold text-primary">
                      ${shop.rent}<span className="text-xs sm:text-sm font-normal text-gray-500">/mo</span>
                    </div>
                  )}
                </div>
                
                <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4 text-xs sm:text-sm text-gray-600">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Square className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                    <span>{shop.size} square meters</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                    <span>{shop.floor} Floor</span>
                  </div>
                  <p className="text-gray-700">{shop.purpose}</p>
                </div>
                
                <div className="flex justify-end space-x-1.5 sm:space-x-2">
                  <button
                    onClick={() => handleEditShop(shop)}
                    className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit shop"
                  >
                    <Edit2 className="h-3 w-3 sm:h-4 sm:w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteShop(shop.id || shop._id)}
                    className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete shop"
                  >
                    <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredShops.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No shops found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search criteria or add a new shop.</p>
            <button
              onClick={handleAddShop}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
            >
              Add First Shop
            </button>
          </div>
        )}

        {/* Shop Modal */}
        {showModal && (
          <ShopModal
            shop={editingShop}
            onClose={() => setShowModal(false)}
            onSave={handleSaveShop}
          />
        )}
      </div>
  );
}